import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { LogOut, TrendingUp, Users, Trophy, Star, Home, ArrowLeft, MessageSquare, Camera, Calculator } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/apiClient";

const STAT_META = [
  { key: "leads_recus", label: "Leads reçus", Icon: Users, color: "#0D6EFD" },
  { key: "demandes_en_cours", label: "Demandes en cours", Icon: TrendingUp, color: "#00C3A7" },
  { key: "projets_gagnes", label: "Projets gagnés", Icon: Trophy, color: "#F59E0B" },
  { key: "avis_moyen", label: "Avis moyen", Icon: Star, color: "#0D6EFD" },
];

export default function Dashboard() {
  const { user, checking, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const isPro = user && (user.user_type === "professionnel" || user.role === "admin");

  useEffect(() => {
    if (!checking && !user) navigate("/");
  }, [checking, user, navigate]);

  useEffect(() => {
    if (isPro) api.get("/pro/dashboard").then(({ data }) => setData(data)).catch(() => {});
  }, [isPro]);

  if (checking || !user) {
    return <div className="min-h-screen bg-[#0A0F1A] flex items-center justify-center text-white/60">Chargement…</div>;
  }

  return (
    <div className="min-h-screen bg-[#0A0F1A]">
      <header className="border-b border-white/10 bg-[#0A0F1A]/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/")} className="text-white/60 hover:text-white" data-testid="dashboard-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <Logo size={26} />
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-white text-sm font-medium">{user.name}</p>
              <p className="text-white/40 text-xs capitalize">{user.role === "admin" ? "Administrateur" : user.user_type}</p>
            </div>
            <Button variant="ghost" onClick={() => { logout(); navigate("/"); }} className="text-white/70 hover:bg-white/10" data-testid="dashboard-logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-white" data-testid="dashboard-welcome">
            Bonjour {user.name.split(" ")[0]} 👋
          </h1>
          <p className="text-white/50 mt-1">
            {isPro ? "Voici votre tableau de bord professionnel." : "Bienvenue dans votre espace ID-BAT PRO."}
          </p>
        </div>

        {isPro && data ? (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="pro-dashboard-stats-leads">
              {STAT_META.map((s) => (
                <div key={s.key} className="rounded-2xl bg-[#121A29] border border-white/10 p-5">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${s.color}22` }}>
                    <s.Icon className="w-5 h-5" style={{ color: s.color }} />
                  </div>
                  <p className="text-2xl font-extrabold text-white">{data.stats[s.key]}</p>
                  <p className="text-white/50 text-sm">{s.label}</p>
                </div>
              ))}
            </div>

            <div className="grid lg:grid-cols-3 gap-6" data-testid="pro-dashboard-tab-pipeline">
              <div className="lg:col-span-2 rounded-2xl bg-[#121A29] border border-white/10 p-6">
                <h3 className="text-white font-semibold mb-4">Évolution des leads</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data.chart}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis dataKey="month" stroke="#8F9CAE" fontSize={12} />
                      <YAxis stroke="#8F9CAE" fontSize={12} />
                      <Tooltip contentStyle={{ background: "#0A0F1A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} />
                      <Line type="monotone" dataKey="leads" stroke="#0D6EFD" strokeWidth={3} dot={{ fill: "#00C3A7", r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="rounded-2xl bg-[#121A29] border border-white/10 p-6">
                <h3 className="text-white font-semibold mb-4">Demandes récentes</h3>
                <div className="space-y-3 max-h-64 overflow-y-auto idb-scroll">
                  {data.recent_leads.length === 0 && <p className="text-white/40 text-sm">Aucune demande pour le moment.</p>}
                  {data.recent_leads.map((l, i) => (
                    <div key={`${l.email}-${i}`} className="rounded-xl bg-white/5 border border-white/5 p-3">
                      <div className="flex items-center justify-between">
                        <p className="text-white text-sm font-medium">{l.name}</p>
                        <Badge className="bg-[#0D6EFD]/15 text-[#0D6EFD] border-0 text-xs">{l.status}</Badge>
                      </div>
                      <p className="text-white/50 text-xs mt-1 line-clamp-2">{l.message}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: "Poser une question", Icon: MessageSquare, hash: "#assistant-btp" },
              { label: "Diagnostic photo", Icon: Camera, hash: "#diagnostic-ia" },
              { label: "Estimer un budget", Icon: Calculator, hash: "#estimation" },
              { label: "Retour à l'accueil", Icon: Home, hash: "#accueil" },
            ].map((c) => (
              <button key={c.label} onClick={() => navigate("/" + c.hash)}
                className="rounded-2xl bg-[#121A29] border border-white/10 hover:border-[#0D6EFD]/50 p-6 text-left transition-colors">
                <div className="w-11 h-11 rounded-xl bg-[#0D6EFD]/15 flex items-center justify-center mb-3">
                  <c.Icon className="w-5 h-5 text-[#00C3A7]" />
                </div>
                <p className="text-white font-medium">{c.label}</p>
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
